import { createContext, useContext } from 'react';

import type { UpgradeReason } from '@/components/billing/upgrade-dialog';
import { useIsOfflineApp } from '@/contexts/offline-mode-context';

export type { UpgradeReason };

export type Upgrade = {
    /** Runs `action` when a slot is free; otherwise opens the limit dialog, which runs `action` once an archive frees a slot. */
    withSlot: (activeCount: number, action: () => void) => void;
    /** Opens the limit dialog directly (e.g. after a server 402). `then`, when given, is what the server
     *  refused: it runs once an archive in the dialog frees a slot, like `withSlot`'s action, and is dropped
     *  if the dialog is dismissed. */
    showLimit: (then?: () => void) => void;
    /** Opens the upgrade dialog for a Pro-only feature. */
    showUpgrade: (reason: UpgradeReason) => void;
};

/** Nothing is enforced here (offline build, no provider): act, and open nothing. */
export const UNLIMITED: Upgrade = {
    withSlot: (_activeCount, action) => action(),
    showLimit: () => {},
    showUpgrade: () => {},
};

/**
 * Filled by `UpgradeProvider` (`upgrade-context.tsx`). It lives apart from the
 * provider so that list hooks can reach the calls without importing the dialogs,
 * which list diagrams through those same hooks.
 */
export const UpgradeContext = createContext<Upgrade>(UNLIMITED);

/**
 * The plan-limit calls. Without a provider, or in the offline build (no plan,
 * no limits), `withSlot` just runs the action and the `show*` calls do nothing.
 */
export function useUpgrade(): Upgrade {
    const offline = useIsOfflineApp();
    const value = useContext(UpgradeContext);
    return offline ? UNLIMITED : value;
}
